import React, { useEffect } from 'react';
import { Container, Typography, Grid, Paper, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CreateIcon from '@mui/icons-material/Create';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import Looks4Icon from '@mui/icons-material/Looks4';

function Home() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: <CompareArrowsIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Resume-Job Matching',
      description: 'Our AI analyzes your resume against job descriptions to identify alignment and gaps, helping you understand exactly how well you match each opportunity.'
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Smart Resume Enhancement',
      description: 'Get AI-powered suggestions to improve your resume and automatically generate optimized versions tailored to specific job descriptions.'
    },
    {
      icon: <CreateIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Cover Letter Generation',
      description: 'Create compelling, personalized cover letters that highlight your relevant experience and skills for each job application.'
    },
    {
      icon: <WorkIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Professional PDF Export',
      description: 'Export your enhanced resume and cover letter in professional PDF format, ready for job submissions.'
    }
  ];

  const steps = [
    {
      icon: <LooksOneIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Add Your Resume',
      description: 'Start by pasting your current resume in text format.'
    },
    {
      icon: <LooksTwoIcon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Add Job Description',
      description: 'Paste the job description you\'re interested in applying for.'
    },
    {
      icon: <Looks3Icon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Get AI Analysis',
      description: 'Our AI analyzes the match and provides detailed enhancement suggestions.'
    },
    {
      icon: <Looks4Icon sx={{ fontSize: 40, color: '#00ffea' }} />,
      title: 'Generate & Download',
      description: 'Generate your optimized resume and cover letter, then download in PDF format.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 4, md: 8 },
              backgroundColor: '#0a0b0e',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 4
              }}
            >
              Land Your Dream Job with AI-Powered Resume Enhancement
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 6,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Transform your job application process with our AI technology. Match your resume to job descriptions, get intelligent enhancement suggestions, and generate tailored cover letters - all in one platform.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')}
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.2rem',
                borderRadius: 50,
                background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #00b8ff 0%, #00ffea 100%)',
                }
              }}
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
            </Button>
          </Paper>
        </Grid>

        {/* Features Section */}
        <Grid item xs={12}>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#0a0b0e',
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      mb: 2
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* How It Works Section */}
        <Grid item xs={12} sx={{ mt: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: '#fff',
              mb: 6
            }}
          >
            How It Works
          </Typography>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={3} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#0a0b0e',
                    borderRadius: 4,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {step.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      mb: 2
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6
                    }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Final CTA Section */}
        <Grid item xs={12} sx={{ mt: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 4, md: 8 },
              backgroundColor: '#0a0b0e',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                color: '#fff',
                mb: 3
              }}
            >
              Ready to Transform Your Job Search?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 4,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Join thousands of successful job seekers who have optimized their applications with our AI-powered platform.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/signup')}
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.2rem',
                borderRadius: 50,
                background: 'linear-gradient(90deg, #00ffea 0%, #00b8ff 100%)',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #00b8ff 0%, #00ffea 100%)',
                }
              }}
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Home; 